import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import projectsData from '../data/projects.json';
import styles from './Projects.module.css';

interface PlatformLink {
  status: string;
  link?: string;
}

interface Project {
  name: string;
  description: string;
  tagline?: string;
  category?: string;
  url: string;
  color: string;
  technologies: string[];
  status: string;
  featured?: boolean;
  web?: PlatformLink;
  googlePlay?: PlatformLink;
  appStore?: PlatformLink;
  privacy?: string;
}

function PlatformLinks({ project, styles }: { project: Project; styles: Record<string, string> }) {
  if (!project.googlePlay && !project.appStore) return null;
  return (
    <div className={styles.platformLinks}>
      {project.googlePlay && (
        project.googlePlay.link && project.googlePlay.link !== '#' ? (
          <a href={project.googlePlay.link} target="_blank" rel="noopener noreferrer" className={styles.storeButton} title="Get it on Google Play">
            <img src="/GooglePlay.png" alt="Get it on Google Play" className={styles.storeButtonImg} />
          </a>
        ) : (
          <span className={styles.platformLinkPlain}><span className={styles.platformLabel}>Android</span> {project.googlePlay.status}</span>
        )
      )}
      {project.appStore && (
        project.appStore.link && project.appStore.link !== '#' ? (
          <a href={project.appStore.link} target="_blank" rel="noopener noreferrer" className={styles.storeButton} title="Download on the App Store">
            <img src="/AppStore.png" alt="Download on the App Store" className={styles.storeButtonImg} />
          </a>
        ) : (
          <span className={styles.platformLinkPlain}><span className={styles.platformLabel}>iOS</span> {project.appStore.status}</span>
        )
      )}
    </div>
  );
}

const Projects: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const projects: Project[] = projectsData as Project[];
  const featuredProjects = projects.filter(p => p.featured);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: [0.4, 0, 0.2, 1] as const
      }
    }
  };

  return (
    <section id="projects" className={styles.projects}>
      <div className="container">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="section-title">Selected Work</h2>
          <p className="section-subtitle">
            Platforms and web applications we're actively building and testing
          </p>
        </motion.div>

        <div className={styles.websiteAppsSection}>
          <motion.div 
            ref={ref}
            className={styles.websiteAppsGrid}
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            {featuredProjects.map((project) => (
              <motion.div
                key={project.name}
                className={styles.websiteAppCard}
                variants={cardVariants}
                whileHover={{ y: -8, scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <div className={styles.cardSidebar} style={{ backgroundColor: project.color }}></div>
                <div className={styles.cardContent}>
                  <div className={styles.cardTop}>
                    <span className={styles.websiteLabel}>{project.category || 'Web App'}</span>
                    <span className={styles.statusBadge} style={{ backgroundColor: project.color }}>
                      {project.status}
                    </span>
                  </div>
                  <h3 className={styles.cardTitle}>{project.name}</h3>
                  {project.tagline && <p className={styles.cardTagline}>{project.tagline}</p>}
                  <p className={styles.cardDescription}>{project.description}</p>
                  <PlatformLinks project={project} styles={styles} />
                  {project.privacy && (
                    <a href={project.privacy} className={styles.cardLink}>
                      Privacy policy
                    </a>
                  )}
                  {project.url !== '#' && (
                    <a 
                      href={project.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={styles.cardLink}
                    >
                      View Project
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M7 17L17 7M17 7H7M17 7V17"/>
                      </svg>
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        <motion.div 
          className={styles.projectsCta}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <p>Want the full details on each project?</p>
          <a href="/projects" className={styles.ctaButton}>
            View All Projects
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;
